(() => {
  'use strict';

  if (window.__MM_SIZE_COLLECTION_PACKER__) return;
  window.__MM_SIZE_COLLECTION_PACKER__ = true;

  const CONFIG_SELECTOR = 'script[data-mm-size-collection-packer-config]';
  const GRID_SELECTOR = '[data-productgrid-items]';
  const ITEM_SELECTOR = '[data-product-item]';
  const PAGINATION_SELECTOR = '.pagination--container';
  const MAX_CONCURRENT_REQUESTS = 3;

  function toPositiveInteger(value, fallback) {
    const number = parseInt(value, 10);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  }

  function readConfig(node) {
    if (!node) return null;

    try {
      const config = JSON.parse(node.textContent || '{}');
      const pageSize = toPositiveInteger(config.pageSize, 24);
      const currentPage = toPositiveInteger(config.currentPage, 1);
      const rawPages = toPositiveInteger(config.rawPages, 1);

      if (!config.collectionUrl || !config.sectionId || rawPages <= 1) {
        return null;
      }

      return {
        collectionUrl: String(config.collectionUrl),
        currentPage,
        pageSize,
        rawPages,
        sectionId: String(config.sectionId),
      };
    } catch (error) {
      console.warn('[size-collection-packer] Invalid config', error);
      return null;
    }
  }

  function getSectionRoot(config, configNode) {
    return (
      document.getElementById(`shopify-section-${config.sectionId}`) ||
      configNode.closest('.shopify-section') ||
      configNode.closest('[data-section-id]') ||
      document
    );
  }

  function getProductKey(item) {
    const wishlistCard = item.querySelector('[data-wishlist-card]');
    const productId = wishlistCard?.getAttribute('data-product-id');
    if (productId) return `product:${productId}`;

    const productLink = item.querySelector('[data-product-page-link], .productitem--title a, a[href*="/products/"]');
    if (productLink) {
      const href = productLink.getAttribute('href') || '';
      return `href:${href.split('?')[0]}`;
    }

    return item.textContent.trim().replace(/\s+/g, ' ').slice(0, 160);
  }

  function collectItems(root) {
    const grid = root.querySelector?.(GRID_SELECTOR) || root;
    return Array.from(grid.querySelectorAll?.(ITEM_SELECTOR) || []);
  }

  function buildSectionUrl(config, page) {
    const url = new URL(window.location.href);
    const collectionUrl = new URL(config.collectionUrl, window.location.origin);

    url.pathname = collectionUrl.pathname;
    url.searchParams.delete('section_id');
    url.searchParams.set('section_id', config.sectionId);

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    return url.toString();
  }

  async function fetchPageItems(config, page) {
    const response = await fetch(buildSectionUrl(config, page), {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(`Page ${page} returned ${response.status}`);
    }

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    return collectItems(documentFragment);
  }

  async function collectPackedItems(config, currentItems) {
    const pages = Array.from({ length: config.rawPages }, (_, index) => index + 1);
    const pageItems = new Map([[config.currentPage, currentItems]]);
    let nextPageIndex = 0;

    async function worker() {
      while (nextPageIndex < pages.length) {
        const page = pages[nextPageIndex];
        nextPageIndex += 1;

        if (pageItems.has(page)) continue;
        pageItems.set(page, await fetchPageItems(config, page));
      }
    }

    const workerCount = Math.min(MAX_CONCURRENT_REQUESTS, pages.length);
    await Promise.all(Array.from({ length: workerCount }, worker));

    const seen = new Set();
    const packedItems = [];

    pages.forEach((page) => {
      (pageItems.get(page) || []).forEach((item) => {
        const key = getProductKey(item);
        if (key && seen.has(key)) return;
        if (key) seen.add(key);
        packedItems.push(item);
      });
    });

    return packedItems;
  }

  function buildPageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    return `${url.pathname}${url.search}${url.hash}`;
  }

  function getPaginationParts(currentPage, totalPages) {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);
    const parts = [];

    sortedPages.forEach((page, index) => {
      if (index > 0 && page - sortedPages[index - 1] > 1) {
        parts.push('ellipsis');
      }
      parts.push(page);
    });

    return parts;
  }

  function makePaginationLink(page, label, className, ariaLabel) {
    const item = document.createElement('li');
    if (className) item.className = className;

    const link = document.createElement('a');
    link.className = 'pagination--item';
    link.href = buildPageUrl(page);
    link.setAttribute('aria-label', ariaLabel || `Go to page ${label}`);
    link.textContent = label;
    item.appendChild(link);

    return item;
  }

  function renderPagination(root, grid, currentPage, totalPages) {
    let nav = root.querySelector(PAGINATION_SELECTOR);

    if (totalPages <= 1) {
      nav?.remove();
      return;
    }

    if (!nav) {
      nav = document.createElement('nav');
      nav.className = 'pagination--container';
      nav.setAttribute('aria-label', 'Pagination');
      grid.insertAdjacentElement('afterend', nav);
    }

    const list = document.createElement('ul');
    list.className = 'pagination--inner';

    if (currentPage > 1) {
      list.appendChild(makePaginationLink(currentPage - 1, 'Previous', 'pagination--previous', 'Go to previous page'));
    }

    getPaginationParts(currentPage, totalPages).forEach((part) => {
      if (part === 'ellipsis') {
        const item = document.createElement('li');
        item.className = 'pagination--ellipsis';
        const span = document.createElement('span');
        span.className = 'pagination--item';
        span.innerHTML = '&hellip;';
        item.appendChild(span);
        list.appendChild(item);
        return;
      }

      if (part === currentPage) {
        const item = document.createElement('li');
        item.className = 'pagination--active';
        item.setAttribute('aria-label', `Page ${part}`);
        item.setAttribute('aria-current', 'true');
        const span = document.createElement('span');
        span.className = 'pagination--item';
        span.textContent = String(part);
        item.appendChild(span);
        list.appendChild(item);
        return;
      }

      list.appendChild(makePaginationLink(part, String(part), '', `Go to page ${part}`));
    });

    if (currentPage < totalPages) {
      list.appendChild(makePaginationLink(currentPage + 1, 'Next', 'pagination--next', 'Go to next page'));
    }

    nav.replaceChildren(list);
  }

  function renderEmptyState(grid) {
    const emptyState = document.createElement('div');
    emptyState.className = 'productgrid--no-results collection-empty-state';
    emptyState.setAttribute('data-collection-empty', '');
    emptyState.style.gridColumn = '1 / -1';

    const message = document.createElement('p');
    message.className = 'collection-empty-state__message';
    message.textContent = 'Sorry this collection is empty';
    emptyState.appendChild(message);

    grid.replaceChildren(emptyState);
  }

  function updateUrlPage(displayPage) {
    const currentUrl = new URL(window.location.href);
    const nextUrl = new URL(window.location.href);

    if (displayPage > 1) {
      nextUrl.searchParams.set('page', String(displayPage));
    } else {
      nextUrl.searchParams.delete('page');
    }
    nextUrl.searchParams.delete('section_id');

    if (nextUrl.toString() !== currentUrl.toString()) {
      window.history.replaceState(window.history.state, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    }
  }

  function renderPackedPage(root, grid, config, packedItems) {
    const totalPages = Math.max(1, Math.ceil(packedItems.length / config.pageSize));
    const displayPage = Math.min(config.currentPage, totalPages);
    const start = (displayPage - 1) * config.pageSize;
    const pageItems = packedItems.slice(start, start + config.pageSize);

    if (!pageItems.length) {
      renderEmptyState(grid);
    } else {
      const fragment = document.createDocumentFragment();
      pageItems.forEach((item) => fragment.appendChild(item));
      grid.replaceChildren(fragment);
    }

    grid.setAttribute('data-mm-size-collection-packed', 'true');
    root.setAttribute('data-mm-size-collection-packed', 'true');
    renderPagination(root, grid, displayPage, totalPages);
    updateUrlPage(displayPage);

    window.RestockCard?.init?.();
    window.Shopify?.PaymentButton?.init?.();
    document.dispatchEvent(new CustomEvent('mm:size-collection:packed', {
      detail: {
        currentPage: displayPage,
        pageSize: config.pageSize,
        totalItems: packedItems.length,
        totalPages,
      },
    }));
  }

  async function packCollection(configNode) {
    const config = readConfig(configNode);
    if (!config) return;

    const root = getSectionRoot(config, configNode);
    const grid = root.querySelector(GRID_SELECTOR);
    if (!grid || grid.hasAttribute('data-mm-size-collection-packing')) return;

    grid.setAttribute('data-mm-size-collection-packing', 'true');

    try {
      const currentItems = collectItems(root);
      const packedItems = await collectPackedItems(config, currentItems);
      renderPackedPage(root, grid, config, packedItems);
    } catch (error) {
      console.warn('[size-collection-packer] Unable to pack collection page', error);
      grid.removeAttribute('data-mm-size-collection-packing');
    }
  }

  function init() {
    document.querySelectorAll(CONFIG_SELECTOR).forEach((configNode) => {
      packCollection(configNode);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
